/**
 * Cliente API de configuracion_cuota (Sprint 13 S13-22).
 *
 * Tabla append-only: cada cambio de monto se inserta como una fila
 * nueva con vigente_desde posterior. No hay DELETE (backend devuelve
 * 405). PATCH solo para correcciones puntuales (typos), no para
 * "cambiar el monto vigente".
 *
 * Sprint 15 S15-01: 6 campos opcionales para metadata de cuenta bancaria
 * del barrio (singleton implicito — la fila vigente tiene la cuenta
 * operativa actual; al rotar tesoreria se crea nueva fila vigente_desde
 * futuro).
 */
import { apiFetch } from './client';

export type TipoCuentaBancaria = 'corriente' | 'vista' | 'ahorro';

export interface ConfiguracionCuotaRead {
  id: string;
  monto_mensual_equivalente: number;
  monto_semestral: number;
  vigente_desde: string;
  aprobado_acta_url: string | null;
  // Tope de controles remotos (CR) activos por casa (RP-06b).
  max_controles_por_lote: number;
  // Régimen de morosidad (convención #20, 2026-10-06).
  meses_plazo_pago: number;
  meses_atraso_moroso: number;
  meses_atraso_suspendible: number;
  regimen_plazo_desde: string;
  cuenta_banco_titular_nombre: string | null;
  cuenta_banco_titular_rut: string | null;
  cuenta_banco_nombre: string | null;
  cuenta_banco_tipo: TipoCuentaBancaria | null;
  cuenta_banco_numero: string | null;
  cuenta_correo_destino: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ConfiguracionCuotaCreate {
  monto_mensual_equivalente: number;
  monto_semestral: number;
  vigente_desde: string;
  max_controles_por_lote?: number;
  meses_plazo_pago?: number;
  meses_atraso_moroso?: number;
  meses_atraso_suspendible?: number;
  regimen_plazo_desde?: string;
  aprobado_acta_url?: string | null;
  cuenta_banco_titular_nombre?: string | null;
  cuenta_banco_titular_rut?: string | null;
  cuenta_banco_nombre?: string | null;
  cuenta_banco_tipo?: TipoCuentaBancaria | null;
  cuenta_banco_numero?: string | null;
  cuenta_correo_destino?: string | null;
}

export interface ConfiguracionCuotaUpdate {
  monto_mensual_equivalente?: number;
  monto_semestral?: number;
  vigente_desde?: string;
  max_controles_por_lote?: number;
  meses_plazo_pago?: number;
  meses_atraso_moroso?: number;
  meses_atraso_suspendible?: number;
  regimen_plazo_desde?: string;
  aprobado_acta_url?: string | null;
  cuenta_banco_titular_nombre?: string | null;
  cuenta_banco_titular_rut?: string | null;
  cuenta_banco_nombre?: string | null;
  cuenta_banco_tipo?: TipoCuentaBancaria | null;
  cuenta_banco_numero?: string | null;
  cuenta_correo_destino?: string | null;
}

export function listConfiguracionesCuota(): Promise<ConfiguracionCuotaRead[]> {
  return apiFetch<ConfiguracionCuotaRead[]>('/configuracion-cuota/');
}

export function getConfiguracionCuotaVigente(): Promise<ConfiguracionCuotaRead> {
  return apiFetch<ConfiguracionCuotaRead>('/configuracion-cuota/vigente');
}

export function crearConfiguracionCuota(
  data: ConfiguracionCuotaCreate,
): Promise<ConfiguracionCuotaRead> {
  return apiFetch<ConfiguracionCuotaRead>('/configuracion-cuota/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function actualizarConfiguracionCuota(
  id: string,
  data: ConfiguracionCuotaUpdate,
): Promise<ConfiguracionCuotaRead> {
  return apiFetch<ConfiguracionCuotaRead>(`/configuracion-cuota/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
